import React from 'react';
import { ServerIcon } from '@heroicons/react/24/solid';

/**
 * HostingToggle Component
 *
 * A mobile-friendly toggle button for enabling and disabling VPN sharing.
 * Provides clear visual feedback and comprehensive accessibility support.
 *
 * ## Features
 * - **Visual States**: Green (active), Blue (inactive), with loading indicators
 * - **Mobile-First Design**: 56px minimum tap target meets accessibility guidelines
 * - **Connection Display**: Shows count of connected friends when hosting
 * - **Loading States**: Animated spinner during activation/deactivation
 * - **Accessibility**: Full ARIA labels, keyboard navigation, screen reader support
 * - **Dark Mode**: Complete theme support with proper contrast ratios
 *
 * ## State Handling
 * - **Active**: VPN sharing is enabled, shows green background
 * - **Inactive**: VPN sharing is disabled, shows blue background
 * - **Loading**: Shows spinner overlay during state transitions
 * - **Disabled**: Grayed out when action is not available
 *
 * ## Platform Support
 * Works seamlessly with both desktop (Electron) and web applications through
 * the unified `onToggle` callback.
 *
 * @see Issue #265 for requirements
 */

interface HostingToggleProps {
  /** Whether hosting is currently active */
  isActive: boolean;
  /** Whether the toggle is in a loading/transitioning state */
  isLoading?: boolean;
  /** Callback when toggle is clicked */
  onToggle: () => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Optional connection count to display when active */
  connectionCount?: number;
}

const HostingToggle: React.FC<HostingToggleProps> = ({
  isActive,
  isLoading = false,
  onToggle,
  disabled = false,
  connectionCount = 0,
}) => {
  const getBackgroundColor = () => {
    if (isActive) {
      return 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600';
    }
    return 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600';
  };

  const getStatusText = () => {
    if (isLoading) {
      return isActive ? 'Deactivating...' : 'Activating...';
    }
    if (isActive) {
      return connectionCount > 0
        ? `${connectionCount} friend${connectionCount === 1 ? '' : 's'} connected`
        : 'VPN Sharing Active';
    }
    return 'Enable VPN Sharing';
  };

  const getAccessibilityLabel = () => {
    if (isLoading) {
      return `VPN sharing is ${isActive ? 'deactivating' : 'activating'}. Please wait.`;
    }
    if (isActive) {
      const connections = connectionCount > 0 ? ` ${connectionCount} friend${connectionCount === 1 ? '' : 's'} connected.` : '';
      return `VPN sharing is active.${connections} Click to deactivate.`;
    }
    return 'VPN sharing is inactive. Click to enable.';
  };

  return (
    <button
      onClick={onToggle}
      disabled={disabled || isLoading}
      title={getAccessibilityLabel()}
      className={`
        w-full rounded-2xl shadow-lg
        min-h-[56px] px-6 py-4
        transition-all duration-300 ease-in-out
        focus:outline-none focus:ring-4 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getBackgroundColor()}
        ${isActive ? 'focus:ring-green-500' : 'focus:ring-blue-500'}
      `}
      aria-pressed={isActive}
      aria-label={getAccessibilityLabel()}
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Icon and Status */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            <ServerIcon className="h-8 w-8 text-white" />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="animate-spin h-8 w-8 text-white opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="text-left">
            <div className="text-white font-bold text-lg">
              {isActive ? 'Hosting' : 'Ready to Host'}
            </div>
            <div className="text-white text-opacity-90 text-sm">
              {getStatusText()}
            </div>
          </div>
        </div>

        {/* Toggle Indicator */}
        <div className="flex-shrink-0">
          <div
            className={`
              w-14 h-8 rounded-full bg-white bg-opacity-30
              flex items-center px-1
              transition-all duration-300
            `}
          >
            <div
              className={`
                w-6 h-6 rounded-full bg-white shadow-md
                transition-transform duration-300
                ${isActive ? 'translate-x-6' : 'translate-x-0'}
              `}
            />
          </div>
        </div>
      </div>
    </button>
  );
};

export default HostingToggle;
